import { Heart, Stethoscope, Shield, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
export type PortalType = 'patient' | 'doctor' | 'admin';
interface PortalBrandingProps {
  portal: PortalType;
  className?: string;
}
const portalConfig = {
  patient: {
    icon: Heart,
    title: 'Patient Portal',
    subtitle: 'Your health journey starts here.',
    description: 'Access your health records, consultations, and AI-powered insights.',
    cta: 'Continue to My Health Dashboard',
    signupCta: 'Start Your Health Journey',
    gradient: 'from-primary/20 via-primary/5 to-transparent',
    iconColor: 'text-primary',
    badge: null
  },
  doctor: {
    icon: Stethoscope,
    title: 'Doctor Portal',
    subtitle: 'Empowering doctors with AI-driven insights.',
    description: 'Access your clinical workspace, patient records, and triage queue.',
    cta: 'Access Doctor Console',
    signupCta: 'Request Professional Access',
    gradient: 'from-secondary/20 via-secondary/5 to-transparent',
    iconColor: 'text-secondary',
    badge: 'Verified Medical Professional'
  },
  admin: {
    icon: Shield,
    title: 'Admin Portal',
    subtitle: 'Restricted access. Authorized personnel only.',
    description: 'System administration, analytics, and platform management.',
    cta: 'Enter Control Panel',
    signupCta: 'Request Admin Access',
    gradient: 'from-destructive/10 via-destructive/5 to-transparent',
    iconColor: 'text-destructive',
    badge: 'Secure Access Required'
  }
};
export function PortalBranding({
  portal,
  className
}: PortalBrandingProps) {
  const config = portalConfig[portal];
  const Icon = config.icon;
  return <div className={cn('text-center space-y-4', className)}>
      {/* Animated Icon */}
      <div className="relative mx-auto w-fit">
        <div className={cn('relative z-10 p-4 rounded-2xl', 'bg-gradient-to-br from-card to-card/50', 'border border-border/50', 'shadow-lg')}>
          <Icon className={cn('h-10 w-10', config.iconColor)} />
        </div>
        {/* Glow Effect */}
        
      </div>

      {/* Title with Activity Indicator */}
      <div className="flex items-center justify-center gap-2">
        <Activity className="h-3 w-3 text-primary animate-pulse" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {config.title}
        </span>
      </div>

      {/* Badge */}
      {config.badge && <div className={cn('inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium', portal === 'doctor' && 'bg-secondary/10 text-secondary border border-secondary/20', portal === 'admin' && 'bg-destructive/10 text-destructive border border-destructive/20')}>
          <Shield className="h-3 w-3" />
          {config.badge}
        </div>}

      {/* Subtitle */}
      <h1 className="text-2xl font-bold text-foreground">
        {config.subtitle}
      </h1>

      {/* Description */}
      <p className="text-sm text-muted-foreground max-w-xs mx-auto">
        {config.description}
      </p>
    </div>;
}
export function getPortalConfig(portal: PortalType) {
  return portalConfig[portal];
}